/**
 * @fileoverview Event Bus Context — single SSE per session.
 *
 * Wraps `eventBusService` and exposes a stable React API so pages can:
 *
 *   const { subscribe } = useEventBus();
 *   useEffect(() => {
 *     return subscribe('new_order', (data) => { … });
 *   }, [subscribe]);
 *
 * Lifecycle:
 *   - On `isAuthenticated` going true, the bus opens with no scope
 *     (personal events only — notifications, session_revoked).
 *   - When a layout enters a scope (e.g. StoreDashboardLayout for a
 *     specific store), it calls `connect({ type: 'store', id })` which
 *     replaces the connection.
 *   - On logout, `disconnect()` is called and the connection closes.
 *
 * Why a singleton service plus React context: the service holds one
 * persistent connection across the entire dashboard, while the context
 * lets components react to state changes (re-render on connect/drop)
 * and register typed subscribers without re-instantiating the bus.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  eventBusService,
  type ConnectionState,
  type EventScope,
} from '@/services/event-bus.service';
import { useAuth } from './AuthContext';

interface EventBusContextValue {
  /** Current connection state. Useful for showing reconnect indicators. */
  state: ConnectionState;
  /**
   * Subscribe a handler to a specific event-type. Returns an unsubscribe
   * function suitable for `useEffect` cleanup. Subscriptions persist
   * across reconnects.
   */
  subscribe: <T = unknown>(
    eventType: string,
    handler: (data: T) => void,
  ) => () => void;
  /**
   * Open or replace the SSE connection. `null` connects with personal
   * channel only; otherwise the scope channel is added (server validates
   * RBAC and 403s if not allowed).
   */
  connect: (scope: EventScope | null) => void;
  /**
   * Register a callback for "stream just reconnected after a drop".
   * Useful for catch-up refetches in pages that may have missed events
   * during the offline window.
   */
  onReconnect: (callback: () => void) => () => void;
}

const EventBusContext = createContext<EventBusContextValue | null>(null);

export function EventBusProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<ConnectionState>(() =>
    eventBusService.getState(),
  );

  // Re-render on state change.
  useEffect(() => eventBusService.onStateChange(setState), []);

  // Open/close on auth flip. Connecting with no scope subscribes only
  // to the personal channel — pages that want a scope channel call
  // `connect({...})` themselves (e.g. StoreDashboardLayout).
  useEffect(() => {
    if (isAuthenticated) {
      eventBusService.connect(null);
      return () => {
        eventBusService.disconnect();
      };
    }
  }, [isAuthenticated]);

  // Stable identity for subscribe — never causes re-renders downstream.
  const subscribeRef = useRef(eventBusService.subscribe.bind(eventBusService));
  const subscribe: EventBusContextValue['subscribe'] = useCallback(
    (eventType, handler) =>
      subscribeRef.current(eventType, handler as (d: unknown) => void),
    [],
  );

  const connect: EventBusContextValue['connect'] = useCallback(
    (scope) => eventBusService.connect(scope),
    [],
  );

  const onReconnect: EventBusContextValue['onReconnect'] = useCallback(
    (callback) => eventBusService.onReconnect(callback),
    [],
  );

  const value = useMemo<EventBusContextValue>(
    () => ({ state, subscribe, connect, onReconnect }),
    [state, subscribe, connect, onReconnect],
  );

  return (
    <EventBusContext.Provider value={value}>
      {children}
    </EventBusContext.Provider>
  );
}

/**
 * Read the bus from any descendant. Throws if used outside the
 * provider — that's a programming error, never a runtime fallback.
 */
export function useEventBus(): EventBusContextValue {
  const ctx = useContext(EventBusContext);
  if (!ctx) {
    throw new Error('useEventBus must be used inside <EventBusProvider>');
  }
  return ctx;
}
